import React, { useEffect, useRef, useState } from "react";
import { api, API, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Building2, Landmark, ImageIcon, Plus, Star, Pencil, Trash2, Signature, Upload, Palette, Check } from "lucide-react";

const emptyCo = {
  company_name: "", tagline: "", gstin: "", cin: "", pan: "", udyam: "",
  address: "", phone: "", email: "",
  bank_name: "", account_no: "", ifsc: "", branch: "", logo_url: "",
  default_invoice_theme: "tally",
};

const THEMES = [
  {
    value: "tally",
    label: "Tally Classic",
    subtitle: "Traditional B&W bordered",
    accent: "border-slate-900 bg-slate-50",
    preview: (
      <div className="border-2 border-slate-900 rounded bg-white overflow-hidden text-[8px]">
        <div className="text-center border-b-2 border-slate-900 py-1 font-bold tracking-widest bg-slate-100">TAX INVOICE</div>
        <div className="grid grid-cols-2 border-b-2 border-slate-900">
          <div className="border-r-2 border-slate-900 p-1"><div className="font-bold">MRM RENEWABLES</div><div>GSTIN: 08AATCM...</div></div>
          <div className="p-1"><div>Invoice: INV-00023</div><div>Dated: 20 Feb 2026</div></div>
        </div>
        <div className="p-1 border-b border-slate-900"><div className="font-bold">Buyer:</div><div>Rakesh Sharma</div></div>
        <div className="p-1 border-b border-slate-900 flex justify-between font-bold"><span>Total</span><span>₹ 3,68,000</span></div>
      </div>
    ),
  },
  {
    value: "modern",
    label: "Modern Amber",
    subtitle: "Clean colored header, soft rows",
    accent: "border-amber-500 bg-amber-50",
    preview: (
      <div className="rounded overflow-hidden bg-white text-[8px] border border-amber-200">
        <div className="bg-gradient-to-r from-amber-500 to-amber-600 text-white p-1.5 flex justify-between">
          <div><div className="font-black">MRM RENEWABLES</div><div className="opacity-80">GSTIN · Phone · Email</div></div>
          <div className="text-right"><div className="text-[7px] uppercase opacity-80">Tax Invoice</div><div className="font-black">INV-00023</div></div>
        </div>
        <div className="p-1.5 border-b border-amber-100"><span className="font-bold">Bill To:</span> Rakesh Sharma</div>
        <div className="p-1.5 flex justify-between bg-amber-50 font-bold text-amber-800"><span>Grand Total</span><span>₹ 3,68,000</span></div>
      </div>
    ),
  },
  {
    value: "corporate",
    label: "Corporate Elegant",
    subtitle: "Slate & gold, executive tone",
    accent: "border-slate-900 bg-slate-900/5",
    preview: (
      <div className="rounded overflow-hidden bg-white text-[8px] border border-slate-300">
        <div className="bg-slate-900 text-white p-1.5 flex justify-between">
          <div><div className="font-bold">MRM RENEWABLES</div><div className="text-slate-300 text-[7px]">GSTIN · PAN</div></div>
          <div className="text-right"><div className="text-amber-400 text-[7px] uppercase tracking-widest">Tax Invoice</div><div className="font-black">INV-00023</div></div>
        </div>
        <div className="p-1.5 bg-slate-50 border-b flex justify-between text-[7px]"><span>Billed To: Rakesh Sharma</span><span>Rajasthan</span></div>
        <div className="p-1.5 bg-slate-900 text-white flex justify-between font-bold"><span className="text-amber-400 text-[7px] uppercase">Grand Total</span><span>₹ 3,68,000</span></div>
      </div>
    ),
  },
];

const emptyFirm = {
  name: "", gstin: "", pan: "", cin: "",
  address: "", phone: "", email: "", state: "Rajasthan (08)",
  bank_name: "", account_no: "", ifsc: "", branch: "",
  logo_url: "", signature_url: "", is_default: false,
};

export default function Settings() {
  const [tab, setTab] = useState("company"); // "company" | "firms"
  const [form, setForm] = useState(emptyCo);
  const [firms, setFirms] = useState([]);
  const [firmOpen, setFirmOpen] = useState(false);
  const [firmForm, setFirmForm] = useState(emptyFirm);
  const [firmEditId, setFirmEditId] = useState(null);
  const [loading, setLoading] = useState(false);
  const logoRef = useRef(null);
  const sigRef = useRef(null);
  const token = typeof window !== "undefined" ? localStorage.getItem("mrm_token") : null;

  useEffect(() => {
    api.get("/settings/company").then(({ data }) => setForm({ ...emptyCo, ...data }));
    api.get("/firms").then(({ data }) => setFirms(data));
  }, []);

  const save = async () => {
    setLoading(true);
    try {
      await api.put("/settings/company", form);
      toast.success("Company settings saved");
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const onLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 700 * 1024) { toast.error("Logo too large — keep under 700KB"); return; }
    const reader = new FileReader();
    reader.onload = () => setForm({ ...form, logo_url: reader.result });
    reader.readAsDataURL(file);
  };

  const F = (key, label, testid) => (
    <div key={key}>
      <Label className="text-xs font-semibold text-slate-600">{label}</Label>
      <Input value={form[key] || ""} onChange={(e) => setForm({ ...form, [key]: e.target.value })} data-testid={testid} className="mt-1" />
    </div>
  );

  const FirmField = (key, label, testid) => (
    <div key={key}>
      <Label className="text-xs font-semibold text-slate-600">{label}</Label>
      <Input value={firmForm[key] || ""} onChange={(e) => setFirmForm({ ...firmForm, [key]: e.target.value })} data-testid={testid} className="mt-1" />
    </div>
  );

  const uploadFirmAsset = async (file, key) => {
    if (!file) return;
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      // fetch as blob via files endpoint and use direct URL with ?auth
      setFirmForm(f => ({ ...f, [key]: data.path }));
      toast.success(`${key === "logo_url" ? "Logo" : "Signature"} uploaded`);
    } catch(e){ toast.error(formatError(e.response?.data?.detail)); }
  };

  const saveFirm = async () => {
    try {
      if (firmEditId) await api.patch(`/firms/${firmEditId}`, firmForm);
      else await api.post("/firms", firmForm);
      toast.success("Firm saved");
      setFirms((await api.get("/firms")).data);
      setFirmOpen(false); setFirmForm(emptyFirm); setFirmEditId(null);
    } catch(e){ toast.error(formatError(e.response?.data?.detail)); }
  };

  const editFirm = (f) => { setFirmForm({...emptyFirm, ...f}); setFirmEditId(f.id); setFirmOpen(true); };
  const delFirm = async (f) => {
    try { await api.delete(`/firms/${f.id}`); toast.success("Firm deleted");
      setFirms((await api.get("/firms")).data);
    } catch(e){ toast.error(formatError(e.response?.data?.detail)); }
  };

  const setAsDefault = async (f) => {
    try {
      await api.patch(`/firms/${f.id}`, { ...f, is_default: true });
      toast.success(`${f.name} is now default billing firm`);
      setFirms((await api.get("/firms")).data);
    } catch(e){ toast.error(formatError(e.response?.data?.detail)); }
  };

  const tabCls = (t) => `px-4 py-2 rounded-lg text-sm font-semibold border ${tab===t?"bg-slate-900 text-white border-slate-900":"bg-white text-slate-700 border-slate-200 hover:bg-slate-50"}`;
  const imgSrc = (p) => p ? (p.startsWith("data:") || p.startsWith("http") || p.startsWith("/") ? p : `${API}/files/${p}?auth=${token}`) : null;

  return (
    <div className="space-y-6 max-w-5xl" data-testid="settings-page">
      <div>
        <h1 className="text-3xl font-extrabold text-slate-900" style={{fontFamily:'Outfit'}}>Company Settings <span className="text-slate-400 text-lg font-medium">/ कंपनी सेटिंग</span></h1>
        <p className="text-slate-500 text-sm">Manage the primary company profile and multiple billing firms.</p>
      </div>

      <div className="flex gap-2 flex-wrap">
        <button onClick={()=>setTab("company")} className={tabCls("company")} data-testid="tab-company">Company Profile</button>
        <button onClick={()=>setTab("firms")} className={tabCls("firms")} data-testid="tab-firms">Billing Firms <span className="ml-1 text-[10px] bg-amber-100 text-amber-800 rounded-full px-1.5 py-0.5">{firms.length}</span></button>
        <button onClick={()=>setTab("themes")} className={tabCls("themes")} data-testid="tab-themes">Invoice Themes</button>
      </div>

      {tab === "company" && (
      <>
      <Card><CardContent className="p-6 space-y-4">
        <div className="flex items-center gap-2 text-slate-900 font-semibold"><Building2 className="w-4 h-4 text-amber-500"/> Company Identity</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {F("company_name","Company Name","set-company-name")}
          {F("tagline","Tagline","set-tagline")}
          {F("gstin","GSTIN","set-gstin")}
          {F("cin","CIN","set-cin")}
          {F("pan","PAN","set-pan")}
          {F("udyam","Udyam No.","set-udyam")}
          {F("phone","Phone","set-phone")}
          {F("email","Email","set-email")}
          <div className="sm:col-span-2">
            <Label className="text-xs font-semibold text-slate-600">Registered Address</Label>
            <Textarea rows={2} value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="set-address" className="mt-1" />
          </div>
        </div>
      </CardContent></Card>

      <Card><CardContent className="p-6 space-y-4">
        <div className="flex items-center gap-2 text-slate-900 font-semibold"><Landmark className="w-4 h-4 text-emerald-600"/> Bank Account (for payments)</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {F("bank_name","Bank Name","set-bank-name")}
          {F("account_no","Account Number","set-account-no")}
          {F("ifsc","IFSC Code","set-ifsc")}
          {F("branch","Branch","set-branch")}
        </div>
      </CardContent></Card>

      <Card><CardContent className="p-6 space-y-4">
        <div className="flex items-center gap-2 text-slate-900 font-semibold"><ImageIcon className="w-4 h-4 text-amber-500"/> Company Logo</div>
        <div className="flex items-center gap-6">
          {form.logo_url ? (
            <img src={form.logo_url} alt="logo" className="h-20 object-contain border border-slate-200 rounded-lg p-2 bg-white" data-testid="logo-preview" />
          ) : (
            <div className="h-20 w-40 flex items-center justify-center text-xs text-slate-400 border border-dashed border-slate-300 rounded-lg">No logo</div>
          )}
          <div>
            <Input type="file" accept="image/*" onChange={onLogo} data-testid="logo-upload-input" className="max-w-xs" />
            <p className="text-xs text-slate-500 mt-1">PNG/JPG under 700KB. Used on all printed documents & sidebar.</p>
          </div>
        </div>
      </CardContent></Card>

      <Button onClick={save} disabled={loading} className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="save-settings-btn">
        {loading ? "Saving..." : "Save Settings"}
      </Button>
      </>
      )}

      {tab === "themes" && (
        <Card><CardContent className="p-6 space-y-5">
          <div className="flex items-center gap-2 text-slate-900 font-semibold"><Palette className="w-4 h-4 text-amber-500"/> Default Tax Invoice Theme</div>
          <p className="text-xs text-slate-500 -mt-2">Yeh theme naye invoices me automatically apply hoga. Har invoice par override bhi kar sakte ho.</p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {THEMES.map(t => {
              const selected = (form.default_invoice_theme || "tally") === t.value;
              return (
                <label key={t.value}
                  className={`cursor-pointer relative rounded-xl border-2 p-4 transition ${selected ? t.accent + " shadow-md" : "border-slate-200 hover:border-slate-300 bg-white"}`}
                  data-testid={`theme-card-${t.value}`}>
                  <input type="radio" className="hidden" checked={selected}
                    onChange={()=>setForm({...form, default_invoice_theme: t.value})}
                    data-testid={`theme-radio-${t.value}`}/>
                  {selected && (
                    <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-emerald-500 text-white flex items-center justify-center shadow-lg">
                      <Check className="w-4 h-4"/>
                    </div>
                  )}
                  <div className="mb-3">{t.preview}</div>
                  <div className="text-sm font-bold text-slate-900">{t.label}</div>
                  <div className="text-[11px] text-slate-500">{t.subtitle}</div>
                </label>
              );
            })}
          </div>

          <Button onClick={save} disabled={loading} className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="save-theme-btn">
            {loading ? "Saving..." : "Save as Default Theme"}
          </Button>
        </CardContent></Card>
      )}

      {tab === "firms" && (
        <>
          <Card><CardContent className="p-6 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2 text-slate-900 font-semibold"><Building2 className="w-4 h-4 text-amber-500"/> Billing Firms</div>
                <p className="text-xs text-slate-500 mt-1">Add multiple firms — tax invoices can be raised from any firm. Star icon = default firm.</p>
              </div>
              <Dialog open={firmOpen} onOpenChange={(v)=>{ setFirmOpen(v); if(!v){setFirmForm(emptyFirm); setFirmEditId(null);}}}>
                <DialogTrigger asChild>
                  <Button className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="add-firm-btn"><Plus className="w-4 h-4 mr-1"/>New Firm</Button>
                </DialogTrigger>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader><DialogTitle>{firmEditId?"Edit":"New"} Billing Firm</DialogTitle></DialogHeader>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {FirmField("name","Firm Name*","firm-name")}
                    {FirmField("gstin","GSTIN","firm-gstin")}
                    {FirmField("pan","PAN","firm-pan")}
                    {FirmField("cin","CIN","firm-cin")}
                    {FirmField("state","State (with code)","firm-state")}
                    {FirmField("phone","Phone","firm-phone")}
                    {FirmField("email","Email","firm-email")}
                    <div className="sm:col-span-2">
                      <Label className="text-xs font-semibold text-slate-600">Registered Address</Label>
                      <Textarea rows={2} value={firmForm.address||""} onChange={(e)=>setFirmForm({...firmForm, address:e.target.value})} data-testid="firm-address" className="mt-1"/>
                    </div>
                    {FirmField("bank_name","Bank Name","firm-bank")}
                    {FirmField("account_no","A/c No.","firm-account")}
                    {FirmField("ifsc","IFSC","firm-ifsc")}
                    {FirmField("branch","Branch","firm-branch")}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
                    <div>
                      <Label className="text-xs font-semibold text-slate-600">Firm Logo</Label>
                      <div className="flex items-center gap-3 mt-1">
                        {firmForm.logo_url ? <img src={imgSrc(firmForm.logo_url)} alt="" className="h-14 object-contain border border-slate-200 rounded p-1 bg-white"/> : <div className="h-14 w-20 border border-dashed border-slate-300 rounded flex items-center justify-center text-[10px] text-slate-400">No logo</div>}
                        <input ref={logoRef} type="file" accept="image/*" className="hidden" onChange={(e)=>uploadFirmAsset(e.target.files?.[0],"logo_url")}/>
                        <Button type="button" variant="outline" size="sm" onClick={()=>logoRef.current?.click()} data-testid="firm-upload-logo"><Upload className="w-3.5 h-3.5 mr-1"/>Upload</Button>
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs font-semibold text-slate-600 flex items-center gap-1"><Signature className="w-3.5 h-3.5"/>Authorised Signature</Label>
                      <div className="flex items-center gap-3 mt-1">
                        {firmForm.signature_url ? <img src={imgSrc(firmForm.signature_url)} alt="" className="h-14 object-contain border border-slate-200 rounded p-1 bg-white"/> : <div className="h-14 w-20 border border-dashed border-slate-300 rounded flex items-center justify-center text-[10px] text-slate-400">No sig</div>}
                        <input ref={sigRef} type="file" accept="image/*" className="hidden" onChange={(e)=>uploadFirmAsset(e.target.files?.[0],"signature_url")}/>
                        <Button type="button" variant="outline" size="sm" onClick={()=>sigRef.current?.click()} data-testid="firm-upload-sig"><Upload className="w-3.5 h-3.5 mr-1"/>Upload</Button>
                      </div>
                    </div>
                  </div>
                  <label className="flex items-center gap-2 mt-2 text-sm">
                    <input type="checkbox" checked={!!firmForm.is_default} onChange={(e)=>setFirmForm({...firmForm, is_default:e.target.checked})} data-testid="firm-is-default"/>
                    Set as default billing firm
                  </label>
                  <DialogFooter>
                    <Button variant="outline" onClick={()=>setFirmOpen(false)}>Cancel</Button>
                    <Button className="bg-amber-500 hover:bg-amber-600" onClick={saveFirm} disabled={!firmForm.name} data-testid="save-firm-btn">Save Firm</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
              {firms.length === 0 && (
                <div className="col-span-2 border-2 border-dashed border-slate-200 rounded-xl p-10 text-center text-sm text-slate-400">
                  No billing firms yet. Click "New Firm" to add your first — e.g. MRM Renewables Energy Pvt Ltd, MRM Solar Traders, etc.
                </div>
              )}
              {firms.map(f=>(
                <div key={f.id} className="border border-slate-200 rounded-xl p-4 relative" data-testid={`firm-card-${f.name}`}>
                  {f.is_default && (
                    <div className="absolute top-2 right-2 flex items-center gap-1 text-xs bg-amber-100 text-amber-800 rounded-full px-2 py-0.5 font-semibold" data-testid="firm-default-badge"><Star className="w-3 h-3 fill-amber-500"/>Default</div>
                  )}
                  <div className="flex items-start gap-3">
                    {f.logo_url ? <img src={imgSrc(f.logo_url)} alt="" className="h-10 object-contain rounded bg-white border border-slate-100"/> : <div className="w-12 h-12 bg-amber-50 rounded flex items-center justify-center"><Building2 className="w-5 h-5 text-amber-600"/></div>}
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-slate-900">{f.name}</div>
                      <div className="text-xs text-slate-500">GSTIN: <span className="font-mono">{f.gstin||"-"}</span></div>
                      <div className="text-xs text-slate-500 truncate">{f.address}</div>
                      <div className="text-xs text-slate-500">Phone: {f.phone||"-"}</div>
                    </div>
                  </div>
                  <div className="flex gap-1 mt-3 justify-end">
                    {!f.is_default && (
                      <Button size="sm" variant="ghost" onClick={()=>setAsDefault(f)} data-testid={`firm-set-default-${f.name}`}><Star className="w-3.5 h-3.5 mr-1"/>Set Default</Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={()=>editFirm(f)} data-testid={`firm-edit-${f.name}`}><Pencil className="w-3.5 h-3.5"/></Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="sm" variant="ghost" className="text-rose-600 hover:bg-rose-50" data-testid={`firm-delete-${f.name}`}><Trash2 className="w-3.5 h-3.5"/></Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader><AlertDialogTitle>Delete firm {f.name}?</AlertDialogTitle>
                          <AlertDialogDescription>Existing invoices will still keep the snapshot but this firm won't be selectable anymore.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction className="bg-rose-600 hover:bg-rose-700" onClick={()=>delFirm(f)}>Delete</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              ))}
            </div>
          </CardContent></Card>
        </>
      )}
    </div>
  );
}
